import { useState, useEffect, memo } from 'react';
import { supabase } from '@/lib/supabase';
import { useComplianceStats } from '@/hooks/useComplianceStats';
import {
  AlertCircle,
  Clock,
  Shield,
  BookOpen,
  Calendar,
  MessageSquare,
  CheckSquare,
  Pin,
  User,
  Folder,
  UserCheck,
  UserPlus
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

// MatrixPreview - uses the single source of truth RPC for compliance calculation
const MatrixPreview = memo(({ projectId, projectIds, previewData }) => {
  const isSingleProject = !projectIds || projectIds.length === 0;
  const singleProjectId = isSingleProject ? projectId : null;

  // Use the new unified compliance stats hook for single projects
  const { data: complianceStats, loading } = useComplianceStats(singleProjectId, {
    enabled: isSingleProject
  });

  // For stacks (multiple projects), we need to aggregate
  const [stackData, setStackData] = useState({
    valid: 0,
    expiring: 0,
    expired: 0,
    missing: 0,
    compliance: 0
  });
  const [stackLoading, setStackLoading] = useState(false);

  useEffect(() => {
    // Only fetch stack data if we have multiple projects
    if (isSingleProject) return;

    const fetchStackData = async () => {
      setStackLoading(true);
      try {
        const ids = projectIds || [];

        // Fetch compliance stats for each project using the RPC
        const results = await Promise.all(
          ids.map(id => supabase.rpc('calculate_compliance_stats', { p_project_id: id }))
        );

        // Aggregate the results
        let totalValid = 0;
        let totalValidCrossSpace = 0;
        let totalExpiring = 0;
        let totalExpiringCrossSpace = 0;
        let totalExpired = 0;
        let totalMissing = 0;
        let totalComplianceSum = 0;
        let requirementCount = 0;

        results.forEach(({ data, error }) => {
          if (error || !data?.overall) return;

          totalValid += data.overall.valid || 0;
          totalValidCrossSpace += data.overall.valid_cross_space || 0;
          totalExpiring += data.overall.expiring || 0;
          totalExpiringCrossSpace += data.overall.expiring_cross_space || 0;
          totalExpired += data.overall.expired || 0;
          totalMissing += data.overall.missing || 0;

          // Add compliance percentage for averaging
          if (data.overall.compliance_percentage !== null) {
            totalComplianceSum += data.overall.compliance_percentage;
            requirementCount++;
          }
        });

        setStackData({
          valid: totalValid + totalValidCrossSpace,
          expiring: totalExpiring + totalExpiringCrossSpace,
          expired: totalExpired,
          missing: totalMissing,
          compliance: requirementCount > 0 ? Math.round(totalComplianceSum / requirementCount) : 0
        });
      } catch (error) {
        console.error('Error fetching stack compliance data:', error);
      } finally {
        setStackLoading(false);
      }
    };

    fetchStackData();
  }, [projectIds, isSingleProject]);

  // Determine which data to display
  const isLoading = isSingleProject ? loading : stackLoading;

  const certData = isSingleProject && complianceStats?.overall
    ? {
        valid: (complianceStats.overall.valid || 0) + (complianceStats.overall.valid_cross_space || 0),
        expiring: (complianceStats.overall.expiring || 0) + (complianceStats.overall.expiring_cross_space || 0),
        expired: complianceStats.overall.expired || 0,
        missing: complianceStats.overall.missing || 0
      }
    : stackData;

  const compliance = isSingleProject && complianceStats?.overall
    ? complianceStats.overall.compliance_percentage || 0
    : stackData.compliance;

  if (isLoading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-4 gap-1.5">
        <div className="bg-green-50 rounded-lg p-1.5 border border-green-200">
          <div className="text-lg font-bold text-green-700">{certData.valid}</div>
          <div className="text-[8px] text-green-600">Valid</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-1.5 border border-amber-200">
          <div className="text-lg font-bold text-amber-700">{certData.expiring}</div>
          <div className="text-[8px] text-amber-600">Expiring</div>
        </div>
        <div className="bg-red-50 rounded-lg p-1.5 border border-red-200">
          <div className="text-lg font-bold text-red-700">{certData.expired}</div>
          <div className="text-[8px] text-red-600">Expired</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
          <div className="text-lg font-bold text-gray-700">{certData.missing}</div>
          <div className="text-[8px] text-gray-600">Missing</div>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>Compliance Rate</span>
          <span className="font-medium">{compliance}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              compliance >= 80 ? 'bg-green-500' : compliance >= 50 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${compliance}%` }}
          />
        </div>
      </div>
      {(certData.expiring > 0 || certData.expired > 0 || certData.missing > 0) && (
        <div className="text-[10px] text-amber-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {certData.expired + certData.expiring + certData.missing} certificates need attention
        </div>
      )}
    </div>
  );
});

// TeamPreview - uses prefetched data when available
const TeamPreview = memo(({ projectId, projectIds, previewData }) => {
  const [teamData, setTeamData] = useState(previewData || {
    members: [],
    total: 0,
    active: 0,
    compliant: 0,
    departments: 0
  });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    // Skip fetch if we have prefetched data
    if (previewData) {
      setTeamData(previewData);
      setLoading(false);
      return;
    }

    const fetchTeamData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data: spaceMembers } = await supabase
          .from('space_members')
          .select(`*, user_profiles!user_id (id, first_name, last_name, email, avatar_url, job_title)`)
          .in('project_id', ids);

        if (spaceMembers) {
          const members = spaceMembers.map(sm => ({
            id: sm.user_id,
            name: `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() || sm.user_profiles.email,
            status: sm.status,
            department: sm.department,
            compliance_percentage: sm.compliance_percentage
          })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

          setTeamData({
            members: members.slice(0, 5),
            total: members.length,
            active: members.filter(m => m.status === 'active').length,
            compliant: members.filter(m => m.compliance_percentage >= 80).length,
            departments: new Set(members.map(m => m.department).filter(Boolean)).size
          });
        }
      } catch (error) {
        console.error('Error fetching team data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-4 gap-1.5">
        <div className="bg-blue-50 rounded-lg p-1.5 border border-blue-200">
          <div className="text-lg font-bold text-blue-700">{teamData.total}</div>
          <div className="text-[8px] text-blue-600">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-1.5 border border-green-200">
          <div className="text-lg font-bold text-green-700">{teamData.active}</div>
          <div className="text-[8px] text-green-600">Active</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-1.5 border border-purple-200">
          <div className="text-lg font-bold text-purple-700">{teamData.compliant}</div>
          <div className="text-[8px] text-purple-600">Compliant</div>
        </div>
        <div className="bg-indigo-50 rounded-lg p-1.5 border border-indigo-200">
          <div className="text-lg font-bold text-indigo-700">{teamData.departments}</div>
          <div className="text-[8px] text-indigo-600">Depts</div>
        </div>
      </div>
      <button className="w-full py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5">
        <UserPlus className="h-3.5 w-3.5 text-gray-600" />
        <span className="text-[11px] font-medium text-gray-700">Add Team Member</span>
      </button>
    </div>
  );
});

// TeamCompliancePreview - uses prefetched data when available
const TeamCompliancePreview = memo(({ projectId, projectIds, previewData }) => {
  const [complianceData, setComplianceData] = useState(previewData || {
    requirements: 0,
    highPriority: 0,
    categories: new Set(),
    riskLevel: null
  });

  useEffect(() => {
    if (previewData) {
      setComplianceData({
        requirements: previewData.requirements || 0,
        highPriority: previewData.high_priority || 0,
        categories: new Set(previewData.categories || []),
        riskLevel: previewData.risk_level || null
      });
      return;
    }

    const fetchComplianceData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data: requirements } = await supabase
          .from('compliance_requirements')
          .select('*')
          .in('project_id', ids);

        const { data: riskData } = await supabase
          .from('compliance_risk_assessment')
          .select('risk_level')
          .in('project_id', ids);

        if (requirements) {
          let highestRisk = null;
          if (riskData?.length > 0) {
            const riskLevels = riskData.map(r => r.risk_level).filter(Boolean);
            if (riskLevels.includes('high')) highestRisk = 'high';
            else if (riskLevels.includes('medium')) highestRisk = 'medium';
            else if (riskLevels.includes('low')) highestRisk = 'low';
          }

          setComplianceData({
            requirements: requirements.length,
            highPriority: requirements.filter(r => r.priority === 'high').length,
            categories: new Set(requirements.map(r => r.category).filter(Boolean)),
            riskLevel: highestRisk
          });
        }
      } catch (error) {
        console.error('Error fetching compliance data:', error);
      }
    };

    fetchComplianceData();
  }, [projectId, projectIds, previewData]);

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-3 mt-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium">{complianceData.requirements} Requirements</span>
        </div>
        {complianceData.riskLevel && (
          <Badge className={`text-xs ${getRiskColor(complianceData.riskLevel)}`}>
            {complianceData.riskLevel} risk
          </Badge>
        )}
      </div>
      {complianceData.highPriority > 0 && (
        <div className="flex items-center gap-2 text-xs text-red-600">
          <AlertCircle className="h-3 w-3" />
          {complianceData.highPriority} high priority items
        </div>
      )}
      {complianceData.categories && complianceData.categories.size > 0 && (
        <div className="flex flex-wrap gap-1">
          {Array.from(complianceData.categories).slice(0, 3).map(cat => (
            <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
          ))}
          {complianceData.categories.size > 3 && (
            <Badge variant="secondary" className="text-xs">+{complianceData.categories.size - 3}</Badge>
          )}
        </div>
      )}
    </div>
  );
});

// SiteCompliancePreview - for site-specific compliance and inspections
const SiteCompliancePreview = memo(({ projectId, projectIds, previewData }) => {
  const [siteData, setSiteData] = useState(previewData || {
    total: 0,
    compliant: 0,
    dueSoon: 0,
    overdue: 0,
    notStarted: 0
  });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setSiteData(previewData);
      setLoading(false);
      return;
    }

    const fetchSiteComplianceData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const isStackView = projectIds && projectIds.length > 0;

        const { data, error } = await supabase.rpc('get_compliance_docs', {
          p_project_id: isStackView ? null : projectId,
          p_project_ids: isStackView ? ids : null,
          p_is_stack_view: isStackView,
          p_personnel_limit: 0,
          p_personnel_offset: 0,
          p_site_limit: 1,
          p_site_offset: 0,
          p_status_filter: null,
          p_category_filter: null,
          p_search_query: null
        });

        if (error) throw error;

        setSiteData(data?.stats?.site || { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 });
      } catch (error) {
        console.error('Error fetching site compliance data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSiteComplianceData();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (siteData.total === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <Shield className="h-8 w-8 text-emerald-200 mb-2" />
          <p className="text-xs text-muted-foreground">No site documents yet</p>
        </div>
      </div>
    );
  }

  const urgentCount = (siteData.overdue || 0) + (siteData.dueSoon || 0);

  return (
    <div className="space-y-2 mt-2">
      <div className="grid grid-cols-2 gap-1.5">
        <div className="bg-green-50 rounded-lg p-2 border border-green-200">
          <div className="text-lg font-bold text-green-700">{siteData.compliant || 0}</div>
          <div className="text-[8px] text-green-600">Compliant</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
          <div className="text-lg font-bold text-amber-700">{siteData.dueSoon || 0}</div>
          <div className="text-[8px] text-amber-600">Due Soon</div>
        </div>
        <div className="bg-red-50 rounded-lg p-2 border border-red-200">
          <div className="text-lg font-bold text-red-700">{siteData.overdue || 0}</div>
          <div className="text-[8px] text-red-600">Overdue</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
          <div className="text-lg font-bold text-gray-700">{siteData.notStarted || 0}</div>
          <div className="text-[8px] text-gray-600">Not Started</div>
        </div>
      </div>
      {urgentCount > 0 && (
        <div className="text-[10px] text-amber-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {urgentCount} document{urgentCount !== 1 ? 's' : ''} need attention
        </div>
      )}
    </div>
  );
});

// QualificationsPreview - uses prefetched data when available
const QualificationsPreview = memo(({ projectId, projectIds, previewData }) => {
  const [stats, setStats] = useState(previewData || { total: 0, high: 0, medium: 0, low: 0 });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setStats(previewData);
      setLoading(false);
      return;
    }

    const fetchRequirements = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data } = await supabase
          .from('compliance_requirements')
          .select('priority, category')
          .in('project_id', ids);

        if (data) {
          setStats({
            total: data.length,
            high: data.filter(r => r.priority === 'high').length,
            medium: data.filter(r => r.priority === 'medium').length,
            low: data.filter(r => r.priority === 'low').length
          });
        }
      } catch (error) {
        console.error('Error fetching requirements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRequirements();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (stats.total === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <BookOpen className="h-8 w-8 text-orange-200 mb-2" />
          <p className="text-xs text-muted-foreground">No requirements defined yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      <div className="grid grid-cols-3 gap-1.5">
        <div className="bg-red-50 rounded-lg p-1.5 border border-red-200">
          <div className="text-base font-bold text-red-700">{stats.high}</div>
          <div className="text-[9px] text-red-600">High</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-1.5 border border-yellow-200">
          <div className="text-base font-bold text-yellow-700">{stats.medium}</div>
          <div className="text-[9px] text-yellow-600">Medium</div>
        </div>
        <div className="bg-green-50 rounded-lg p-1.5 border border-green-200">
          <div className="text-base font-bold text-green-700">{stats.low}</div>
          <div className="text-[9px] text-green-600">Low</div>
        </div>
      </div>
      <div className="text-center text-[10px] text-muted-foreground">{stats.total} total requirements</div>
    </div>
  );
});

// BookingsPreview - uses prefetched data when available
const BookingsPreview = memo(({ projectId, projectIds, previewData }) => {
  const [kanbanData, setKanbanData] = useState(previewData || { pending: 0, accepted: 0, rejected: 0, draft: 0, live: 0, booked: 0, total: 0 });

  useEffect(() => {
    if (previewData) {
      setKanbanData(previewData);
      return;
    }

    const fetchBookings = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data } = await supabase.from('training_quotes').select('status').in('project_id', ids);

        if (data) {
          setKanbanData({
            draft: data.filter(q => q.status === 'draft').length,
            live: data.filter(q => q.status === 'live').length,
            booked: data.filter(q => q.status === 'booked').length,
            total: data.length
          });
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };

    fetchBookings();
  }, [projectId, projectIds, previewData]);

  if (kanbanData.total === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <Calendar className="h-8 w-8 text-purple-200 mb-2" />
          <p className="text-xs text-muted-foreground">No training quotes yet</p>
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'draft', label: 'Draft', count: kanbanData.draft, color: 'bg-gray-100 border-gray-200 text-gray-700' },
    { key: 'live', label: 'Live', count: kanbanData.live, color: 'bg-amber-100 border-amber-200 text-amber-700' },
    { key: 'booked', label: 'Booked', count: kanbanData.booked, color: 'bg-green-100 border-green-200 text-green-700' }
  ];

  return (
    <div className="space-y-2 mt-2">
      <div className="grid grid-cols-3 gap-1.5">
        {columns.map((column) => (
          <div key={column.key} className="flex flex-col">
            <div className="text-[10px] font-medium text-gray-600 mb-1 truncate">{column.label}</div>
            <div className={`rounded-md border p-2 text-center ${column.color}`}>
              <div className="text-lg font-bold">{column.count}</div>
              <div className="text-[9px] opacity-80">{column.count === 1 ? 'quote' : 'quotes'}</div>
            </div>
          </div>
        ))}
      </div>
      {kanbanData.live > 0 && (
        <div className="text-[10px] text-amber-600 flex items-center gap-1 mt-1">
          <Clock className="h-2.5 w-2.5" />
          {kanbanData.live} awaiting response from trainers
        </div>
      )}
    </div>
  );
});

// ChatPreview - uses prefetched data when available
const ChatPreview = memo(({ projectId, projectIds, previewData }) => {
  const [messages, setMessages] = useState(previewData?.recent_messages || []);
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData?.recent_messages) {
      setMessages(previewData.recent_messages);
      setLoading(false);
      return;
    }

    const fetchChatData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data: conversations } = await supabase
          .from('conversations')
          .select('id')
          .in('space_id', ids)
          .eq('type', 'space');

        if (conversations?.length > 0) {
          const { data: messageData } = await supabase
            .from('messages')
            .select(`id, content, created_date, sender:sender_id(id, first_name, last_name, display_name, avatar_url)`)
            .in('conversation_id', conversations.map(c => c.id))
            .order('created_date', { ascending: false })
            .limit(2);
          setMessages(messageData || []);
        }
      } catch (error) {
        console.error('Error fetching chat data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChatData();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <MessageSquare className="h-8 w-8 text-indigo-200 mb-2" />
          <p className="text-xs text-muted-foreground">Start a conversation with your team</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 mt-2">
      {messages.map((message) => (
        <div key={message.id} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-1.5">
            <Avatar className="h-4 w-4 flex-shrink-0 mt-0.5">
              <AvatarImage src={message.sender?.avatar_url} />
              <AvatarFallback><User className="h-2 w-2" /></AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-medium text-muted-foreground mb-0.5">
                {message.sender?.display_name || `${message.sender?.first_name || ''} ${message.sender?.last_name || ''}`.trim() || 'Unknown'}
              </p>
              <p className="text-[10px] line-clamp-1 break-words">{message.content}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

// MessageBoardPreview - displays post statistics and recent posts
const MessageBoardPreview = memo(({ projectId, projectIds, previewData }) => {
  const [postData, setPostData] = useState(previewData || {
    total_posts: 0,
    pinned_posts: 0,
    posts_this_week: 0,
    total_comments: 0,
    unique_authors: 0,
    recent_posts: []
  });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setPostData(previewData);
      setLoading(false);
      return;
    }

    const fetchPostData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];

        // Fetch posts with stats
        const { data: posts } = await supabase
          .from('message_board_posts')
          .select(`id, title, created_date, pinned, post_type, created_by, author:created_by(id, first_name, last_name, display_name, avatar_url), comments:message_board_comments(count)`)
          .in('project_id', ids)
          .order('pinned', { ascending: false })
          .order('created_date', { ascending: false });

        if (posts) {
          const now = new Date();
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

          const totalComments = posts.reduce((sum, p) => sum + (p.comments?.[0]?.count || 0), 0);
          const uniqueAuthors = new Set(posts.map(p => p.created_by).filter(Boolean)).size;

          setPostData({
            total_posts: posts.length,
            pinned_posts: posts.filter(p => p.pinned).length,
            posts_this_week: posts.filter(p => new Date(p.created_date) >= weekAgo).length,
            total_comments: totalComments,
            unique_authors: uniqueAuthors,
            recent_posts: posts.slice(0, 2).map(p => ({
              id: p.id,
              title: p.title,
              pinned: p.pinned,
              post_type: p.post_type,
              created_date: p.created_date,
              author: p.author,
              comment_count: p.comments?.[0]?.count || 0
            }))
          });
        }
      } catch (error) {
        console.error('Error fetching post data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPostData();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // No posts yet
  if (postData.total_posts === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <MessageSquare className="h-8 w-8 text-cyan-200 mb-2" />
          <p className="text-xs text-muted-foreground">No posts yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-1">
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Posts</div>
          <div className="rounded-md border p-1 text-center bg-cyan-100 border-cyan-200 text-cyan-700">
            <div className="text-base font-bold">{postData.total_posts}</div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Comments</div>
          <div className="rounded-md border p-1 text-center bg-blue-100 border-blue-200 text-blue-700">
            <div className="text-base font-bold">{postData.total_comments}</div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Pinned</div>
          <div className="rounded-md border p-1 text-center bg-amber-100 border-amber-200 text-amber-700">
            <div className="text-base font-bold">{postData.pinned_posts}</div>
          </div>
        </div>
      </div>

      {/* Recent posts */}
      {postData.recent_posts && postData.recent_posts.length > 0 && (
        <div className="space-y-1">
          {postData.recent_posts.map((post) => (
            <div key={post.id} className="p-1 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-1.5">
                <Avatar className="h-3.5 w-3.5 flex-shrink-0 mt-0.5">
                  <AvatarImage src={post.author?.avatar_url} />
                  <AvatarFallback><User className="h-2 w-2" /></AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    {post.pinned && <Pin className="h-2 w-2 text-amber-600 flex-shrink-0" />}
                    <p className="text-[9px] font-medium truncate">{post.title}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[8px] text-muted-foreground">
                    <span className="truncate">{post.author?.display_name || `${post.author?.first_name || ''} ${post.author?.last_name || ''}`.trim() || 'Unknown'}</span>
                    <span>•</span>
                    <span className="flex items-center gap-0.5">
                      <MessageSquare className="h-1.5 w-1.5" />
                      {post.comment_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity indicator */}
      {postData.posts_this_week > 0 && (
        <div className="text-[10px] text-cyan-600 flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" />
          {postData.posts_this_week} post{postData.posts_this_week !== 1 ? 's' : ''} this week
        </div>
      )}
    </div>
  );
});

// TasksPreview (formerly ChecklistsPreview) - displays workflow/task statistics
const ChecklistsPreview = memo(({ projectId, projectIds, previewData }) => {
  const [taskData, setTaskData] = useState(previewData || {
    total_workflows: 0,
    draft_workflows: 0,
    total_runs: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    total_tasks: 0,
    pending_tasks: 0,
    active_tasks: 0,
    completed_tasks: 0,
    overdue_tasks: 0
  });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setTaskData(previewData);
      setLoading(false);
      return;
    }

    const fetchTaskData = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];

        // Fetch workflows
        const { data: workflows } = await supabase
          .from('workflows')
          .select('status')
          .in('project_id', ids);

        // Fetch workflow runs
        const { data: runs } = await supabase
          .from('checklists_workflow_runs')
          .select('id, status')
          .in('project_id', ids);

        // Fetch task runs for these workflow runs
        let taskStats = { total: 0, pending: 0, active: 0, completed: 0, overdue: 0 };
        if (runs && runs.length > 0) {
          const runIds = runs.map(r => r.id);
          const { data: tasks } = await supabase
            .from('checklists_workflow_task_runs')
            .select('status, due_date')
            .in('workflow_run_id', runIds);

          if (tasks) {
            const now = new Date();
            taskStats = {
              total: tasks.length,
              pending: tasks.filter(t => t.status === 'pending').length,
              active: tasks.filter(t => t.status === 'in_progress').length,
              completed: tasks.filter(t => t.status === 'completed').length,
              overdue: tasks.filter(t => t.status !== 'completed' && t.due_date && new Date(t.due_date) < now).length
            };
          }
        }

        setTaskData({
          total_workflows: workflows?.filter(w => w.status === 'active').length || 0,
          draft_workflows: workflows?.filter(w => w.status === 'draft').length || 0,
          total_runs: runs?.length || 0,
          in_progress: runs?.filter(r => r.status === 'in_progress').length || 0,
          completed: runs?.filter(r => r.status === 'completed').length || 0,
          cancelled: runs?.filter(r => r.status === 'cancelled').length || 0,
          total_tasks: taskStats.total,
          pending_tasks: taskStats.pending,
          active_tasks: taskStats.active,
          completed_tasks: taskStats.completed,
          overdue_tasks: taskStats.overdue
        });
      } catch (error) {
        console.error('Error fetching task data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // No workflows or runs yet
  if (taskData.total_workflows === 0 && taskData.total_runs === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <CheckSquare className="h-8 w-8 text-emerald-200 mb-2" />
          <p className="text-xs text-muted-foreground">No workflows yet</p>
        </div>
      </div>
    );
  }

  // Calculate task completion percentage
  const taskCompletionPercentage = taskData.total_tasks > 0
    ? Math.round((taskData.completed_tasks / taskData.total_tasks) * 100)
    : 0;

  return (
    <div className="space-y-2 mt-2">
      {/* Workflow runs status */}
      <div className="grid grid-cols-3 gap-1">
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Active</div>
          <div className="rounded-md border p-1 text-center bg-blue-100 border-blue-200 text-blue-700">
            <div className="text-base font-bold">{taskData.in_progress}</div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Complete</div>
          <div className="rounded-md border p-1 text-center bg-green-100 border-green-200 text-green-700">
            <div className="text-base font-bold">{taskData.completed}</div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[9px] font-medium text-gray-600 mb-0.5 truncate">Workflows</div>
          <div className="rounded-md border p-1 text-center bg-emerald-100 border-emerald-200 text-emerald-700">
            <div className="text-base font-bold">{taskData.total_workflows}</div>
          </div>
        </div>
      </div>

      {/* Task progress bar */}
      {taskData.total_tasks > 0 && (
        <div className="space-y-0.5">
          <div className="flex justify-between text-[9px] text-gray-600">
            <span>{taskData.completed_tasks}/{taskData.total_tasks} tasks</span>
            <span className="font-medium">{taskCompletionPercentage}%</span>
          </div>
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${taskCompletionPercentage}%` }} />
          </div>
        </div>
      )}

      {/* Alerts for overdue or pending tasks */}
      {taskData.overdue_tasks > 0 && (
        <div className="text-[10px] text-red-600 flex items-center gap-1">
          <AlertCircle className="h-2.5 w-2.5" />
          {taskData.overdue_tasks} overdue task{taskData.overdue_tasks !== 1 ? 's' : ''}
        </div>
      )}
      {taskData.active_tasks > 0 && taskData.overdue_tasks === 0 && (
        <div className="text-[10px] text-blue-600 flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" />
          {taskData.active_tasks} task{taskData.active_tasks !== 1 ? 's' : ''} in progress
        </div>
      )}
    </div>
  );
});

// DocsAndFilesPreview - displays compliance documents overview (personnel certificates & site documents)
const DocsAndFilesPreview = memo(({ projectId, projectIds, previewData }) => {
  const [stats, setStats] = useState(previewData || {
    personnel: { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0 },
    site: { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 }
  });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setStats(previewData);
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const isStackView = projectIds && projectIds.length > 0;

        const { data, error } = await supabase.rpc('get_compliance_docs', {
          p_project_id: isStackView ? null : projectId,
          p_project_ids: isStackView ? ids : null,
          p_is_stack_view: isStackView,
          p_personnel_limit: 1,
          p_personnel_offset: 0,
          p_site_limit: 1,
          p_site_offset: 0,
          p_status_filter: null,
          p_category_filter: null,
          p_search_query: null
        });

        if (error) throw error;

        setStats({
          personnel: data?.stats?.personnel || { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0 },
          site: data?.stats?.site || { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 }
        });
      } catch (error) {
        console.error('Error fetching docs stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [projectId, projectIds, previewData]);

  // Calculate urgent counts
  const personnelUrgent = (stats.personnel?.expired || 0) + (stats.personnel?.expiring || 0);
  const siteUrgent = (stats.site?.overdue || 0) + (stats.site?.dueSoon || 0);
  const totalUrgent = personnelUrgent + siteUrgent;

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Empty state
  if (stats.personnel?.total === 0 && stats.site?.total === 0) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <Folder className="h-8 w-8 text-purple-200 mb-2" />
          <p className="text-xs text-muted-foreground">No documents yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      {/* Personnel Certificates Folder */}
      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-gray-700">User Certificates</span>
              <span className="text-xs font-bold text-gray-700">{stats.personnel?.total || 0}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {stats.personnel?.expired > 0 && (
                <span className="text-[9px] text-red-600">{stats.personnel.expired} expired</span>
              )}
              {stats.personnel?.expiring > 0 && (
                <span className="text-[9px] text-amber-600">{stats.personnel.expiring} expiring</span>
              )}
              {stats.personnel?.missing > 0 && (
                <span className="text-[9px] text-gray-400">{stats.personnel.missing} missing</span>
              )}
              {personnelUrgent === 0 && stats.personnel?.valid > 0 && (
                <span className="text-[9px] text-green-600">{stats.personnel.valid} valid</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Site Documents Folder */}
      <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5 text-blue-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-gray-700">Site Documents</span>
              <span className="text-xs font-bold text-gray-700">{stats.site?.total || 0}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {stats.site?.overdue > 0 && (
                <span className="text-[9px] text-red-600">{stats.site.overdue} overdue</span>
              )}
              {stats.site?.dueSoon > 0 && (
                <span className="text-[9px] text-amber-600">{stats.site.dueSoon} due soon</span>
              )}
              {stats.site?.notStarted > 0 && (
                <span className="text-[9px] text-gray-400">{stats.site.notStarted} not started</span>
              )}
              {siteUrgent === 0 && stats.site?.compliant > 0 && (
                <span className="text-[9px] text-green-600">{stats.site.compliant} compliant</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alert for items needing attention */}
      {totalUrgent > 0 && (
        <div className="text-[10px] text-amber-600 flex items-center gap-1">
          <AlertCircle className="h-2.5 w-2.5" />
          {totalUrgent} item{totalUrgent !== 1 ? 's' : ''} need attention
        </div>
      )}
    </div>
  );
});

// CheckinsPreview - uses prefetched data when available
const CheckinsPreview = memo(({ projectId, projectIds, previewData }) => {
  const [stats, setStats] = useState(previewData || { on_site_now: 0, total_today: 0, avgTime: '0h 0m' });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setStats({
        on_site_now: previewData.on_site_now || 0,
        total_today: previewData.total_today || 0,
        avgTime: '0h 0m'
      });
      setLoading(false);
      return;
    }

    const fetchCheckInStats = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const [{ data: activeCheckIns }, { data: todayCheckIns }] = await Promise.all([
          supabase.from('site_checkins').select('*').in('project_id', ids).is('check_out_time', null),
          supabase.from('site_checkins').select('*').in('project_id', ids).gte('check_in_time', new Date(new Date().setHours(0, 0, 0, 0)).toISOString())
        ]);

        const completedToday = (todayCheckIns || []).filter(c => c.check_out_time);
        let avgTimeStr = '0h 0m';
        if (completedToday.length > 0) {
          const totalMs = completedToday.reduce((sum, c) => sum + (new Date(c.check_out_time) - new Date(c.check_in_time)), 0);
          const avgMs = totalMs / completedToday.length;
          avgTimeStr = `${Math.floor(avgMs / 3600000)}h ${Math.floor((avgMs % 3600000) / 60000)}m`;
        }

        setStats({ on_site_now: activeCheckIns?.length || 0, total_today: todayCheckIns?.length || 0, avgTime: avgTimeStr });
      } catch (error) {
        console.error('Error fetching check-in stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckInStats();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-teal-700">{stats.on_site_now}</div>
            <div className="text-[10px] text-teal-600 font-medium">Currently On-Site</div>
          </div>
          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
            <UserCheck className="h-5 w-5 text-teal-600" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
          <div className="text-[9px] text-gray-600 mb-0.5">Total Today</div>
          <div className="text-base font-bold text-gray-700">{stats.total_today}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
          <div className="text-[9px] text-gray-600 mb-0.5">Avg. Time</div>
          <div className="text-base font-bold text-gray-700">{stats.avgTime}</div>
        </div>
      </div>
    </div>
  );
});

// AttendancePreview - uses prefetched data when available
const AttendancePreview = memo(({ projectId, projectIds, previewData }) => {
  const [stats, setStats] = useState(previewData || { total_members: 0, on_shift: 0, on_leave: 0, pending_requests: 0 });
  const [loading, setLoading] = useState(!previewData);

  useEffect(() => {
    if (previewData) {
      setStats(previewData);
      setLoading(false);
      return;
    }

    const fetchAttendanceStats = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const { data: teamMembers } = await supabase
          .from('space_members')
          .select('user_id')
          .in('project_id', ids)
          .eq('status', 'active');

        const userIds = teamMembers?.map(m => m.user_id) || [];
        const today = new Date().toISOString().split('T')[0];

        const [{ data: pendingLeave }, { data: onLeaveToday }, { data: shiftsToday }] = await Promise.all([
          supabase.from('leave_requests').select('id').in('user_id', userIds).eq('status', 'pending'),
          supabase.from('leave_requests').select('id').in('user_id', userIds).eq('status', 'approved').lte('start_date', today).gte('end_date', today),
          supabase.from('shift_patterns').select('user_id').in('user_id', userIds).eq('is_active', true)
        ]);

        setStats({
          total_members: userIds.length,
          on_shift: shiftsToday?.length || 0,
          on_leave: onLeaveToday?.length || 0,
          pending_requests: pendingLeave?.length || 0
        });
      } catch (error) {
        console.error('Error fetching attendance stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceStats();
  }, [projectId, projectIds, previewData]);

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      <div className="grid grid-cols-2 gap-1.5">
        <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
          <div className="text-lg font-bold text-blue-700">{stats.total_members}</div>
          <div className="text-[8px] text-blue-600">Team Members</div>
        </div>
        <div className="bg-green-50 rounded-lg p-2 border border-green-200">
          <div className="text-lg font-bold text-green-700">{stats.on_shift || 0}</div>
          <div className="text-[8px] text-green-600">On Shift</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
          <div className="text-lg font-bold text-amber-700">{stats.on_leave}</div>
          <div className="text-[8px] text-amber-600">On Leave</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-2 border border-purple-200">
          <div className="text-lg font-bold text-purple-700">{stats.pending_requests}</div>
          <div className="text-[8px] text-purple-600">Pending</div>
        </div>
      </div>
      {stats.pending_requests > 0 && (
        <div className="text-[10px] text-purple-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {stats.pending_requests} leave request{stats.pending_requests !== 1 ? 's' : ''} awaiting approval
        </div>
      )}
    </div>
  );
});

// CalendarPreview - uses prefetched data when available
const CalendarPreview = memo(({ projectId, projectIds, previewData }) => {
  const [events, setEvents] = useState(previewData?.events || []);
  const [loading, setLoading] = useState(!previewData);
  const [currentDate] = useState(new Date());

  useEffect(() => {
    if (previewData?.events) {
      setEvents(previewData.events);
      setLoading(false);
      return;
    }

    const fetchEvents = async () => {
      try {
        const ids = projectIds && projectIds.length > 0 ? projectIds : [projectId];
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        const { data } = await supabase
          .from('schedule_events')
          .select('id, title, event_date, event_time, color')
          .in('project_id', ids)
          .gte('event_date', startOfMonth.toISOString().split('T')[0])
          .lte('event_date', endOfMonth.toISOString().split('T')[0])
          .order('event_date');

        if (data) setEvents(data);
      } catch (error) {
        console.error('Error fetching calendar events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [projectId, projectIds, currentDate, previewData]);

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(i);
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dayDateString = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toISOString().split('T')[0];
    return events.filter(event => event.event_date === dayDateString);
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  };

  const days = getDaysInMonth();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="space-y-3 mt-2">
        <div className="flex flex-col items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-pink-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 mt-2">
      <div className="text-center">
        <div className="text-[10px] font-semibold text-pink-700">{monthName}</div>
      </div>
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
            <div key={idx} className="text-center text-[7px] font-medium text-gray-500 py-0.5">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, idx) => {
            const dayEvents = getEventsForDay(day);
            const today = isToday(day);
            return (
              <div
                key={idx}
                className={`h-5 flex items-center justify-center text-[8px] border-r border-b border-gray-100 last:border-r-0 relative
                  ${!day ? 'bg-gray-50' : dayEvents.length > 0 ? '' : 'bg-white'}
                  ${today ? 'ring-1 ring-inset ring-pink-500 font-bold text-pink-700 z-10' : 'text-gray-600'}`}
                style={day && dayEvents.length > 0 ? { backgroundColor: dayEvents[0].color || '#ec4899', color: 'white', fontWeight: '600' } : {}}
              >
                {day && <span>{day}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

// Main ToolPreview component - routes to specific preview based on toolKey
// previewData is already tool-specific when passed from SpaceToolCard
export const ToolPreview = memo(({ toolKey, projectId, projectIds, previewData }) => {
  switch (toolKey) {
    case 'matrix':
      return <MatrixPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'team':
      return <TeamPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'team_compliance':
      return <TeamCompliancePreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'site_compliance':
      return <SiteCompliancePreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'qualifications':
      return <QualificationsPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'bookings':
      return <BookingsPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'calendar':
      return <CalendarPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'checklists':
    case 'tasks':
      return <ChecklistsPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'chat':
      return <ChatPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'message_board':
      return <MessageBoardPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'docs':
      return <DocsAndFilesPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'checkins':
      return <CheckinsPreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    case 'attendance':
      return <AttendancePreview projectId={projectId} projectIds={projectIds} previewData={previewData} />;
    default:
      return null;
  }
});
